import { safe } from '@w/utils'
import { isLazyStories, isLazyStory, isStoryWrapper } from './story'
import type { LazyStory, Story, StoryTreeNode, StoryWrapper } from './story'

export type StorybookNavNode = {
  id: string // . separated path (eg section.page.component.story)
  name: string
  depth: number
  type: 'section' | 'page' | 'component' | 'story'
  children?: StorybookNavNode[]
  parentId?: string
  story?: Story | LazyStory
  wrappers?: StoryWrapper[]
}

/**
 * Determines the type of a node based on its children
 * @param node The node to determine the type for
 * @param isLeaf Whether the node is a leaf (story)
 * @returns The node type: 'section', 'page', 'component', or 'story'
 */
function determineNodeType(
  isLeaf: boolean,
  childrenTypes: ('section' | 'page' | 'component' | 'story')[] = [],
): 'section' | 'page' | 'component' | 'story' {
  if (isLeaf) return 'story'

  // If it has no children, default to component
  if (childrenTypes.length === 0) return 'component'

  // If it has any section children, it's a section
  if (childrenTypes.includes('section')) return 'section'

  // If it has any page children, it's a section
  if (childrenTypes.includes('page')) return 'section'

  // If it has any component children, it's a page
  if (childrenTypes.includes('component')) return 'page'

  // If it only has story children, it's a component
  return 'component'
}

export function convertToStorybookNavTree(
  node: StoryTreeNode,
  parentId = '',
  depth = 0,
  idAndNode = new Map<string, StorybookNavNode>(),
  wrappers: StoryWrapper[] = [],
) {
  const navigation: StorybookNavNode[] = []
  const markedAsSection = new Set<string>()

  // First pass: create all nodes without determining their types yet
  const tempNodes: (Omit<StorybookNavNode, 'type'> & {
    value: StoryTreeNode | Story | LazyStory
    isLeaf: boolean
    childrenTypes?: ('section' | 'page' | 'component' | 'story')[]
  })[] = []

  for (const [key, value] of Object.entries(node)) {
    const [name, type] = key.split('-')

    const currentId = parentId ? `${parentId}.${name}` : name
    if (isLazyStories(value)) {
      throw new TypeError(
        `Story tree node "${currentId}" is a lazyStories module. Select its named story exports explicitly.`,
      )
    }
    if (type === 'section') markedAsSection.add(currentId)
    const displayName = splitPascalCase(name)
    const isLeaf = typeof value === 'function' || isLazyStory(value)
    const childValue = isStoryWrapper(value) ? value.stories : value
    const childWrappers = isStoryWrapper(value)
      ? [...wrappers, value]
      : wrappers

    // Check if the value is a Story function (leaf node)
    if (isLeaf) {
      const leaf = value as Story | LazyStory
      if (leaf.hidden) {
        // Direct URL only: indexed for lookup, absent from navigation and search.
        idAndNode.set(currentId, { id: currentId, name: displayName, depth, type: 'story', parentId, story: leaf, wrappers })
        continue
      }
      tempNodes.push({
        id: currentId,
        name: displayName,
        depth,
        value: value as Story | LazyStory,
        isLeaf: true,
        parentId,
        story: value as Story | LazyStory,
        wrappers,
      })
    } else {
      // It's a nested object, process it recursively
      const childCount = Object.keys(childValue as StoryTreeNode).length
      const { navigation: childNavigation } = convertToStorybookNavTree(
        // oxlint-disable-next-line typescript/no-unnecessary-type-assertion -- cast load-bearing for tsc narrowing
        childValue as StoryTreeNode,
        currentId,
        depth + 1,
        idAndNode,
        childWrappers,
      )
      // A group whose every story is hidden is itself hidden.
      if (childCount > 0 && childNavigation.length === 0) {
        idAndNode.set(currentId, { id: currentId, name: displayName, depth, type: 'component', parentId })
        continue
      }

      tempNodes.push({
        id: currentId,
        name: displayName,
        depth,
        value: childValue as StoryTreeNode,
        isLeaf: false,
        children: childNavigation.length > 0 ? childNavigation : undefined,
        childrenTypes: childNavigation.map((child) => child.type),
        parentId,
      })
    }
  }

  // Second pass: determine node types based on their children
  for (const tempNode of tempNodes) {
    const isSection = markedAsSection.has(tempNode.id)
    const nodeType = isSection
      ? ('section' as const)
      : determineNodeType(tempNode.isLeaf, tempNode.childrenTypes)

    const node: StorybookNavNode = {
      id: tempNode.id,
      name: tempNode.name,
      depth: tempNode.depth,
      type: nodeType,
      children: tempNode.children,
      parentId: tempNode.parentId,
      story: tempNode.story,
      wrappers: tempNode.wrappers,
    }

    navigation.push(node)

    idAndNode.set(node.id, node)
  }

  return makeNavTree(navigation, idAndNode)
}

function makeNavTree(
  navigation: StorybookNavNode[],
  idAndNode: Map<string, StorybookNavNode>,
) {
  return {
    navigation,
    getStory: (id: string) => idAndNode.get(id)?.story,
    getNode: (id: string) => idAndNode.get(id),
    getNodeParent: (id: string) => {
      const node = idAndNode.get(id)
      if (!node) throw new Error('No story found')
      const parentId = node.parentId
      // oxlint-disable-next-line unicorn/no-useless-undefined -- required for consistent-return: other branch returns a value
      if (!parentId) return undefined
      return idAndNode.get(parentId)
    },
    getNodeAndParent: (id: string) => {
      const node = idAndNode.get(id)
      // oxlint-disable-next-line unicorn/no-useless-undefined -- required for consistent-return: other branch returns a value
      if (!node) return undefined
      const parent = node.parentId ? idAndNode.get(node.parentId) : undefined
      return { node, parent }
    },
    getFirstStoryId: (parentId?: string) => {
      if (!parentId) return getFirstStoryId(navigation)

      const parent = idAndNode.get(parentId)
      if (!parent) throw new Error('No parent found')
      if (parent.type === 'story') return parentId
      return getFirstStoryId(parent.children ?? [])
    },
    bfs: () => traverseBfs(navigation),
    _debug: () => {
      return renderTreeToString(navigation)
    },
  }
}
export type NavTree = ReturnType<typeof makeNavTree>

function splitPascalCase(str: string): string {
  return str.replaceAll(/([A-Z])/gu, ' $1').trim()
}

function renderTreeToString(nav: StorybookNavNode[]): string {
  const lines: string[] = []

  function renderNode(node: StorybookNavNode, indent = 0) {
    const prefix = '  '.repeat(indent)
    lines.push(`${prefix}${node.type.toUpperCase()} ${node.name}`)

    if (node.children) {
      for (const child of node.children) {
        renderNode(child, indent + 1)
      }
    }
  }

  for (const node of nav) {
    renderNode(node)
  }

  return lines.join('\n')
}

// dfs
function getFirstStoryId(nav: StorybookNavNode[]) {
  const stack = [...nav]
  while (stack.length > 0) {
    const node = safe(stack.shift())
    if (node.type === 'story') return node.id
    if (node.children) stack.unshift(...node.children)
  }
  throw new Error('No story found')
}

function traverseBfs(nav: StorybookNavNode[]): StorybookNavNode[] {
  const result: StorybookNavNode[] = []
  const queue = [...nav]

  while (queue.length > 0) {
    const node = safe(queue.shift())
    result.push(node)
    if (node.children) queue.push(...node.children)
  }

  return result
}
